import { createContext, useContext } from 'react';
import {
    AppliedFilter,
    OutgoingFilter,
    SortOption,
    UserDetails,
    Project,
} from '../types';

// Your existing state interface
export interface ProjectLibraryState {
    currentPage: number;
    totalPages: number;
    prevPage: number | null;
    nextPage: number | null;
    numItems: number;
    appliedSortOption: SortOption;
    appliedFilters: AppliedFilter[];
    filterCounts: OutgoingFilter[];
    projects: Project[];
    userDetailsMapping: { [user_id: string]: UserDetails | null };
    searchString: string;
    isLoading: boolean;
}

// New interface for setters
export interface ProjectLibrarySetters {
    setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
    setTotalPages: React.Dispatch<React.SetStateAction<number>>;
    setPrevPage: React.Dispatch<React.SetStateAction<number | null>>;
    setNextPage: React.Dispatch<React.SetStateAction<number | null>>;
    setNumItems: React.Dispatch<React.SetStateAction<number>>;
    setAppliedSortOption: React.Dispatch<React.SetStateAction<SortOption>>;
    setAppliedFilters: React.Dispatch<React.SetStateAction<AppliedFilter[]>>;
    setFilterCounts: React.Dispatch<React.SetStateAction<OutgoingFilter[]>>;
    setProjects: React.Dispatch<React.SetStateAction<Project[]>>;
    setUserDetailsMapping: React.Dispatch<
        React.SetStateAction<{ [user_id: string]: UserDetails | null }>
    >;
    setSearchString: React.Dispatch<React.SetStateAction<string>>;
    setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

// Update the context to include both state and setters
export interface ProjectLibraryContextType {
    state: ProjectLibraryState;
    setters: ProjectLibrarySetters;
    refresh: (firstPage?: boolean, showSpinner?: boolean) => Promise<void>;
}

export const ProjectLibraryContext =
    createContext<ProjectLibraryContextType | null>(null);

export function useProjectLibraryContext() {
    const libraryContext = useContext(ProjectLibraryContext);

    if (libraryContext === null) {
        throw new Error(
            'useProjectLibraryContext must be used with a ProjectLibraryContext',
        );
    }

    return libraryContext;
}
